import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-input font-medium transition-colors duration-150 ease-out disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-text-tertiary disabled:border-transparent active:brightness-[0.98]',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-on-accent font-semibold hover:bg-accent-hover',
        secondary: 'bg-surface text-text-primary border border-border-strong hover:bg-surface-muted',
        ghost: 'bg-transparent text-text-secondary hover:bg-surface-muted hover:text-text-primary',
        danger: 'bg-danger-tint text-danger border border-danger/30 hover:bg-danger-tint/80',
        // W-09 / W-31d: the confirmation that re-writes the numbers renders solid,
        // because a tint button is too quiet for "122 hareketi oluştur".
        dangerSolid: 'bg-danger text-white font-semibold hover:brightness-110',
        dashed: 'bg-surface text-text-primary border border-dashed border-border-strong hover:bg-surface-muted',
      },
      size: {
        sm: 'h-7 px-2.5 text-[12px]',
        md: 'h-9 px-3.5 text-[13px]',
        lg: 'h-11 px-5 text-[14px]',
        xl: 'h-12 px-5 text-[14px]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="size-3 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { 'aria-label': string }>(function IconButton(
  { className, size = 'md', variant = 'ghost', ...props },
  ref,
) {
  const sq = size === 'sm' ? 'w-7 px-0' : size === 'lg' ? 'w-11 px-0' : 'w-9 px-0';
  return <Button ref={ref} size={size} variant={variant} className={cn(sq, className)} {...props} />;
});
